import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../../ui';
import { fmtTestDate } from '../../../lib/projectToday';
import InlineEdit from './InlineEdit';
import LogoUpload from './LogoUpload';
import Toggle from './Toggle';
import StatusBadge from './StatusBadge';
import AlertRow from './AlertRow';
import WorkWeekPicker from './WorkWeekPicker';

/**
 * A job's Overview (Duncan 2026-10-26: "I want it to look SUPER nice, with modern and new looking elements while
 * prioritizing user ease of use and access"; the mockup he OK'd, all four parts):
 *  - Job at a glance: time used, work done (the schedule's rollup) and billed, each a bar; and Finish setting up -- what
 *    the job still lacks, each a chip that opens that field;
 *  - Details, Team (people with their role and email) and Timeline (the dates on a bar, today marked), Settings, and
 *    Project health when something needs attention;
 *  - save as you go: a field saves when you leave it (`saveField`), no Save / Reset bar.
 */

const CSS = `
.ov { display: flex; flex-direction: column; gap: 16px; max-width: 100%; }
.ov-top { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 16px; }
.ov-cols { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 16px; align-items: start; }
.ov-col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.ov-card { background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 14px; box-shadow: var(--shadow-sm);
  min-width: 0; scroll-margin-top: 140px; }
.ov-head { display: flex; align-items: center; gap: 8px; padding: 15px 18px 0; font-size: 14.5px; font-weight: 600; color: var(--text-1);
  letter-spacing: -0.01em; }
.ov-head > svg { color: var(--text-3); flex: none; }
.ov-side { margin-left: auto; font-size: 12.5px; font-weight: 500; color: var(--text-3); letter-spacing: 0; }
.ov-body { padding: 12px 18px 17px; }
.ov-meters { display: grid; gap: 14px; }
.ov-meter-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 13.5px; font-weight: 500;
  color: var(--text-1); }
.ov-meter-row > span:last-child { color: var(--text-3); font-size: 12.5px; font-weight: 400; text-align: right; }
.ov-bar { position: relative; height: 8px; border-radius: 99px; background: var(--bg-surface-2); border: 1px solid var(--border-subtle);
  margin-top: 7px; }
.ov-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 99px; background: var(--accent);
  transition: width .6s cubic-bezier(.3, 1, .3, 1); }
.ov-tick { position: absolute; top: -5px; bottom: -5px; width: 2px; border-radius: 2px; background: var(--warning); }
.ov-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ov-chip { display: inline-flex; align-items: center; gap: 5px; height: 28px; padding: 0 11px; border-radius: 999px;
  border: 1px dashed var(--border-strong); background: transparent; color: var(--text-2); font: 500 12.5px var(--font-sans);
  cursor: pointer; white-space: nowrap; transition: border-color .15s, color .15s, background .15s; }
.ov-chip:hover, .ov-chip:focus-visible { border-style: solid; border-color: var(--accent); color: var(--accent-text); background: var(--accent-soft);
  outline: none; }
.ov-chip.is-key { border-color: color-mix(in oklch, var(--warning) 70%, transparent); color: var(--warning); }
.ov-saveline { display: flex; justify-content: flex-end; margin: -6px 0 -4px; }
.ov-saved { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--text-3); }
.ov-person { display: flex; align-items: center; gap: 10px; padding: 6px 8px; margin: 0 -8px; border-radius: 10px; }
.ov-person:hover { background: var(--bg-hover); }
.ov-person .ov-x { opacity: 0; }
.ov-person:hover .ov-x, .ov-person:focus-within .ov-x { opacity: 1; }
.ov-avatar { width: 32px; height: 32px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font: 600 12px var(--font-sans); background: var(--accent-soft); color: var(--accent-text); flex: none; }
.ov-iconbtn { width: 28px; height: 28px; border-radius: 8px; border: 1px solid transparent; background: transparent; color: var(--text-3);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex: none; text-decoration: none; }
.ov-iconbtn:hover, .ov-iconbtn:focus-visible { color: var(--text-1); border-color: var(--border-subtle); background: var(--bg-surface-2); outline: none; }
.ov-label { display: flex; align-items: baseline; gap: 8px; font-size: 12px; font-weight: 500; color: var(--text-3); margin: 0 0 2px 10px; }
.ov-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 14px; }
.ov-pick { position: absolute; z-index: 30; top: calc(100% + 6px); left: 0; width: 280px; max-width: 80vw; background: var(--bg-surface);
  border: 1px solid var(--border-default); border-radius: 12px; box-shadow: var(--shadow-lift); padding: 6px; }
.ov-pick input { width: 100%; box-sizing: border-box; height: 32px; padding: 0 10px; border-radius: 8px; border: 1px solid var(--border-default);
  background: var(--bg-surface); color: var(--text-1); font: 400 13.5px var(--font-sans); outline: none; margin-bottom: 4px; }
.ov-pick input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
.ov-pick-list { max-height: 260px; overflow-y: auto; }
.ov-pick-row { display: flex; align-items: center; gap: 9px; width: 100%; padding: 6px 8px; border: none; border-radius: 8px; background: transparent;
  color: var(--text-1); font: 500 13.5px var(--font-sans); text-align: left; cursor: pointer; }
.ov-pick-row:hover, .ov-pick-row:focus-visible { background: var(--bg-hover); outline: none; }
.ov-track { position: relative; height: 34px; margin: 6px 6px 4px; }
.ov-rail { position: absolute; left: 0; right: 0; top: 15px; height: 4px; border-radius: 9px; background: var(--bg-surface-2);
  border: 1px solid var(--border-subtle); }
.ov-railfill { position: absolute; left: 0; top: 15px; height: 4px; border-radius: 9px; background: var(--accent); }
.ov-dot { position: absolute; top: 10px; width: 14px; height: 14px; margin-left: -7px; border-radius: 50%; border: 3px solid var(--bg-surface); }
.ov-today { position: absolute; top: 2px; bottom: 2px; width: 3px; margin-left: -1.5px; border-radius: 3px; background: var(--warning); }
.ov-signed { position: absolute; top: 11px; width: 11px; height: 11px; margin-left: -5.5px; transform: rotate(45deg); border-radius: 2px;
  background: var(--text-3); border: 2px solid var(--bg-surface); }
.ov-row { display: flex; align-items: flex-start; gap: 12px; padding: 4px 0; }
.ov-sep { height: 1px; background: var(--border-subtle); margin: 12px 0; }
@media (max-width: 1100px) { .ov-top, .ov-cols { grid-template-columns: minmax(0, 1fr); } }
.ov-dates { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); gap: 8px; align-items: end; }
.ov-todaywords { font-size: 12px; color: var(--warning); text-align: center; padding-bottom: 9px; white-space: nowrap; }
@media (max-width: 560px) {
  .ov-grid2 { grid-template-columns: minmax(0, 1fr); }
  /* A phone has no room for Start, today and End on one line: today goes under them, so the dates aren't cut off. */
  .ov-dates { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .ov-todaywords { grid-column: 1 / -1; order: 3; text-align: left; padding: 0 10px; }
}
@media (prefers-reduced-motion: reduce) { .ov-fill { transition: none; } }
`;

const DAY = 86400000;
const ymd = (v) => { const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v || '')); return m ? m[1] : null; };
const dayNo = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / DAY;
const ymdOf = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const fmtDay = (v) => (ymd(v) ? fmtTestDate(ymd(v)) : '');
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const initialsOf = (full) => {
  const parts = String(full || '').split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const namesOf = (v) => String(v || '').split(',').map((s) => s.trim()).filter(Boolean);
/** The job's length in words, on the Timeline's header. */
const durationLabel = (start, end) => {
  if (!start || !end) return null;
  const days = dayNo(end) - dayNo(start);
  if (!(days > 0)) return null;
  const months = days / 30.44;
  return months < 1.5 ? plural(days, 'day') : `${Math.round(months)} months`;
};

const XIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const Head = ({ icon, title, side }) => (
  <div className="ov-head">
    <Icon name={icon} size={16} />
    <span>{title}</span>
    {side != null && <span className="ov-side">{side}</span>}
  </div>
);

/** One bar of Job at a glance; `tick`: where the clock is, on the work bar. */
function Meter({ label, right, pct, tick, tickTitle }) {
  return (
    <div>
      <div className="ov-meter-row"><span>{label}</span><span>{right}</span></div>
      <div className="ov-bar" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamp(pct))}>
        <div className="ov-fill" style={{ width: `${clamp(pct)}%` }} />
        {tick != null && <span className="ov-tick" style={{ left: `calc(${clamp(tick)}% - 1px)` }} title={tickTitle} />}
      </div>
    </div>
  );
}

/** Where the job stands on the clock: the days from its start to its end, today among them. */
function clockOf(startIso, endIso, todayIso) {
  if (!startIso || !endIso) return null;
  const total = dayNo(endIso) - dayNo(startIso);
  if (!(total > 0)) return null;
  const used = dayNo(todayIso) - dayNo(startIso);
  const left = dayNo(endIso) - dayNo(todayIso);
  return { total, used, left, pct: clamp((used / total) * 100) };
}

function Glance({ clock, work, billed, contract, collected, isTest, todayIso }) {
  let timeRight = 'Add the start and end dates';
  if (clock) {
    if (clock.used < 0) timeRight = `starts in ${plural(-clock.used, 'day')}`;
    else if (clock.left < 0) timeRight = `ended ${plural(-clock.left, 'day')} ago`;
    else timeRight = `${plural(clock.left, 'day')} left · ${Math.round(clock.pct)}% of the time`;
  }
  const workPct = work == null ? null : Number(work);
  return (
    <div className="ov-card">
      <Head icon="pulse" title="Job at a glance" side={isTest ? `as of ${fmtDay(todayIso)}` : null} />
      <div className="ov-body ov-meters">
        <Meter label="Time" right={timeRight} pct={clock ? clock.pct : 0} />
        <Meter
          label="Work done"
          // It counts each task by the SOV dollars linked to it (projects.js work_done_percent); the Schedule's % counts working
          // days, so with subtasks the two can differ -- the words say which this is (Duncan 2026-10-30, the six).
          right={workPct == null ? 'No schedule yet' : `${workPct < 10 ? workPct.toFixed(1) : Math.round(workPct)}% done, by SOV dollars`}
          pct={workPct || 0}
          tick={clock && clock.used >= 0 ? clock.pct : null}
          tickTitle={clock ? `Where the clock is: ${Math.round(clock.pct)}% of the time used` : undefined}
        />
        <Meter label="Billed" right={`${money(billed)} of ${money(contract)} · collected ${money(collected)}`} pct={contract > 0 ? (billed / contract) * 100 : 0} />
      </div>
    </div>
  );
}

/* What a job should have filled in, in the order the chips show (Duncan's OK'd mockup: "Finish setting up"). */
const SETUP = [
  { key: 'logo_url', label: 'Logo' },
  { key: 'client_name', label: 'Client' },
  { key: 'project_manager', label: 'Project manager' },
  { key: 'construction_manager', label: 'Construction manager' },
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'project_developer', label: 'Project developer' },
  { key: 'start_date', label: 'Start date' },
  { key: 'end_date', label: 'End date' },
  { key: 'contract_date', label: 'Contract date' },
  { key: 'contract_for', label: 'Contract for', key2: 'it prints on AIA pay apps' },
  { key: 'description', label: 'Description' },
];

function Setup({ edited, onAsk }) {
  const missing = SETUP.filter((s) => !String((edited && edited[s.key]) || '').trim());
  const done = SETUP.length - missing.length;
  if (!missing.length) {
    return (
      <div className="ov-card">
        <Head icon="sparkles" title="Set up" side={`${done} of ${SETUP.length}`} />
        <div className="ov-body" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-2)' }}>
          <Icon name="check" size={15} style={{ color: 'var(--success)' }} />Everything on this job is filled in.
        </div>
      </div>
    );
  }
  return (
    <div className="ov-card">
      <Head icon="sparkles" title="Finish setting up" side={`${done} of ${SETUP.length}`} />
      <div className="ov-body">
        <div className="ov-bar" style={{ marginTop: 0, marginBottom: 12 }}><div className="ov-fill" style={{ width: `${(done / SETUP.length) * 100}%` }} /></div>
        <div className="ov-chips">
          {missing.map((s) => (s.key === 'logo_url' ? (
            // A label for the logo's own file input: the picker opens in every browser.
            <label key={s.key} htmlFor="ov-logo-input" className="ov-chip" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('ov-logo-input')?.click(); } }}>
              <Icon name="plus" size={12} />{s.label}
            </label>
          ) : (
            <button key={s.key} type="button" className={`ov-chip${s.key2 ? ' is-key' : ''}`} onClick={() => onAsk(s.key)}
              title={s.key2 ? `${s.label}: ${s.key2}` : `Add the ${s.label.toLowerCase()}`}>
              <Icon name="plus" size={12} />{s.label}
            </button>
          )))}
        </div>
      </div>
    </div>
  );
}

/** "Saving…", "Saved", or what went wrong: the save-as-you-go state, over the cards. */
function SaveState({ state }) {
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    if (!state || state.status !== 'saved') return undefined;
    setFresh(true);
    const t = setTimeout(() => setFresh(false), 2500);
    return () => clearTimeout(t);
  }, [state]);
  if (state && state.status === 'saving') return <span className="ov-saved" aria-live="polite"><Icon name="refresh-cw" size={13} />Saving…</span>;
  if (state && state.status === 'error') return <span className="ov-saved" aria-live="assertive" style={{ color: 'var(--danger)' }}><Icon name="alert-triangle" size={13} />{state.text}</span>;
  if (fresh) return <span className="ov-saved" aria-live="polite" style={{ color: 'var(--success)' }}><Icon name="check" size={13} />Saved</span>;
  return <span className="ov-saved"><Icon name="check" size={13} />Changes save as you go</span>;
}

/** A "+ Role" chip that opens the people to pick from (typing narrows it, Enter takes the first); `openSignal` opens it
    from a setup chip. It opens upward when the window has no room below. */
function PersonAdd({ label, people, exclude, onPick, openSignal }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [q, setQ] = useState('');
  const wrap = useRef(null);
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);
  useEffect(() => {
    if (!open) return undefined;
    setQ('');
    const r = wrap.current && wrap.current.getBoundingClientRect();
    setUp(!!r && window.innerHeight - r.bottom < 340 && r.top > 340);
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const shown = people.filter((p) => !exclude.includes(p.name) && (!q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <span ref={wrap} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" className="ov-chip" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="plus" size={12} />{label}
      </button>
      {open && (
        <div className="ov-pick" role="dialog" aria-label={`Pick the ${label.toLowerCase()}`}
          style={up ? { top: 'auto', bottom: 'calc(100% + 6px)' } : undefined}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name" aria-label="Type a name"
            onKeyDown={(e) => { if (e.key === 'Enter' && shown.length) { e.preventDefault(); setOpen(false); onPick(shown[0].name); } }} />
          <div className="ov-pick-list">
            {shown.length ? shown.map((p) => (
              <button key={p.id || p.name} type="button" className="ov-pick-row" onClick={() => { setOpen(false); onPick(p.name); }}>
                <span className="ov-avatar" style={{ width: 26, height: 26, fontSize: 11 }}>{initialsOf(p.name)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                {p.role_label && <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400 }}>{p.role_label}</span>}
              </button>
            )) : <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-3)' }}>No one by that name.</div>}
          </div>
        </div>
      )}
    </span>
  );
}

const ROLES = [
  { key: 'project_manager', label: 'Project manager', many: true },
  { key: 'construction_manager', label: 'Construction manager' },
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'project_developer', label: 'Project developer' },
];

function Team({ edited, pmList, saveField, ask, cardRef }) {
  const emailOf = (name) => (pmList.find((p) => p.name === name) || {}).email || null;
  const rows = [];
  ROLES.forEach((r) => namesOf(edited && edited[r.key]).forEach((name) => rows.push({ role: r, name })));
  const remove = (role, name) => {
    const left = namesOf(edited[role.key]).filter((n) => n !== name);
    saveField(role.key, role.many ? left.join(', ') : '');
  };
  return (
    <div className="ov-card" ref={cardRef}>
      <Head icon="team" title="Team" side={rows.length ? plural(rows.length, 'person') .replace('persons', 'people') : null} />
      <div className="ov-body">
        {rows.map(({ role, name }) => {
          const email = emailOf(name);
          return (
            <div key={`${role.key}:${name}`} className="ov-person">
              <span className="ov-avatar" aria-hidden="true">{initialsOf(name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{role.label}</div>
              </div>
              {email && <a className="ov-iconbtn" href={`mailto:${email}`} title={`Email ${email}`} aria-label={`Email ${name}`}><Icon name="mail" size={14} /></a>}
              <button type="button" className="ov-iconbtn ov-x" onClick={() => remove(role, name)} title={`Take ${name} off as ${role.label.toLowerCase()}`}
                aria-label={`Remove ${name} as ${role.label.toLowerCase()}`}><XIcon /></button>
            </div>
          );
        })}
        <div className="ov-chips" style={{ marginTop: rows.length ? 10 : 0 }}>
          {ROLES.filter((r) => r.many || !namesOf(edited && edited[r.key]).length).map((r) => (
            <PersonAdd
              key={r.key}
              label={r.label}
              people={pmList}
              exclude={namesOf(edited && edited[r.key])}
              openSignal={ask.key === r.key ? ask.n : 0}
              onPick={(name) => saveField(r.key, r.many ? [...namesOf(edited[r.key]), name].join(', ') : name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** The job's dates on a bar: start and end, today marked, the signing date a diamond when it's set. */
function Track({ startIso, endIso, todayIso, contractIso }) {
  const clock = clockOf(startIso, endIso, todayIso);
  if (!clock) {
    return <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '6px 0 2px' }}>Pick the start and end dates to see the job on a timeline.</div>;
  }
  const at = (iso) => clamp(((dayNo(iso) - dayNo(startIso)) / clock.total) * 100);
  const todayAt = at(todayIso);
  return (
    <div className="ov-track" aria-hidden="true">
      <div className="ov-rail" />
      <div className="ov-railfill" style={{ width: `${todayAt}%` }} />
      <span className="ov-dot" style={{ left: '0%', background: 'var(--accent)' }} />
      <span className="ov-dot" style={{ left: '100%', background: clock.left < 0 ? 'var(--accent)' : 'var(--border-strong)' }} />
      {contractIso && <span className="ov-signed" style={{ left: `${at(contractIso)}%` }} title={`Signed ${fmtDay(contractIso)}`} />}
      <span className="ov-today" style={{ left: `${todayAt}%` }} title={`Today ${fmtDay(todayIso)}`} />
    </div>
  );
}

function Timeline({ edited, todayIso, isTest, saveField, ask, cardRef, projectId, workWeek, onWorkWeekChanged }) {
  const startIso = ymd(edited && edited.start_date);
  const endIso = ymd(edited && edited.end_date);
  const contractIso = ymd(edited && edited.contract_date);
  const clock = clockOf(startIso, endIso, todayIso);
  const range = {
    start: edited && edited.start_date, finish: edited && edited.end_date,
    onStart: (iso) => saveField('start_date', iso), onFinish: (iso) => saveField('end_date', iso),
  };
  let todayWords = `Today ${fmtDay(todayIso)}`;
  if (isTest) todayWords = `As of ${fmtDay(todayIso)}`;
  if (clock && clock.used < 0) todayWords += ` · starts in ${plural(-clock.used, 'day')}`;
  return (
    <div className="ov-card" ref={cardRef}>
      <Head icon="clock" title="Timeline" side={durationLabel(startIso, endIso)} />
      <div className="ov-body">
        <Track startIso={startIso} endIso={endIso} todayIso={todayIso} contractIso={contractIso} />
        <div className="ov-dates">
          <div>
            <div className="ov-label">Start</div>
            <InlineEdit type="date" value={edited?.start_date || ''} display={fmtDay(edited?.start_date)} placeholder="Pick a start date"
              ariaLabel="Edit start date" onChange={(v) => saveField('start_date', v)} calendar={{ mode: 'start', ...range }}
              openSignal={ask.key === 'start_date' ? ask.n : 0} />
          </div>
          <div className="ov-todaywords">{todayWords}</div>
          <div style={{ textAlign: 'right' }}>
            <div className="ov-label" style={{ justifyContent: 'flex-end', marginRight: 10 }}>End</div>
            <InlineEdit type="date" align="right" value={edited?.end_date || ''} display={fmtDay(edited?.end_date)} placeholder="Pick an end date"
              ariaLabel="Edit end date" onChange={(v) => saveField('end_date', v)} calendar={{ mode: 'finish', ...range }}
              openSignal={ask.key === 'end_date' ? ask.n : 0} />
          </div>
        </div>
        <div className="ov-sep" />
        <div className="ov-grid2" style={{ alignItems: 'end' }}>
          <div>
            {/* The signing date (#66): Finance → BoMoCo shows it. Sending it on to CE is a separate step. */}
            <div className="ov-label">Contract date</div>
            <InlineEdit type="date" value={edited?.contract_date || ''} display={fmtDay(edited?.contract_date)} placeholder="Pick the signing date"
              ariaLabel="Edit contract date" onChange={(v) => saveField('contract_date', v)}
              openSignal={ask.key === 'contract_date' ? ask.n : 0} />
          </div>
          <div>
            <div className="ov-label">Work week</div>
            <div style={{ paddingLeft: 10 }}><WorkWeekPicker projectId={projectId} value={workWeek} onChanged={onWorkWeekChanged} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Details({ edited, saveField, ask, cardRef, logoUploading, onLogoUpload, onRemoveLogo, contractVal }) {
  const forLen = String(edited?.contract_for || '').length;
  return (
    <div className="ov-card" ref={cardRef}>
      <div className="ov-body" style={{ paddingTop: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <LogoUpload size={64} inputId="ov-logo-input" src={edited?.logo_url || null} uploading={logoUploading} onUpload={onLogoUpload} onRemove={onRemoveLogo} />
          <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 6 }}>
            <InlineEdit big value={edited?.project_name || ''} placeholder="Untitled project" ariaLabel="Edit project name"
              onChange={(v) => saveField('project_name', v)} />
            <div className="ov-grid2">
              <div>
                <div className="ov-label">Project number</div>
                <InlineEdit mono value={edited?.project_number || ''} placeholder="e.g. WOODS-L-01" ariaLabel="Edit project number"
                  onChange={(v) => saveField('project_number', v)} />
              </div>
              <div>
                <div className="ov-label">Client</div>
                <InlineEdit value={edited?.client_name || ''} placeholder="Client name" ariaLabel="Edit client name"
                  onChange={(v) => saveField('client_name', v)} openSignal={ask.key === 'client_name' ? ask.n : 0} />
              </div>
            </div>
          </div>
        </div>
        <div className="ov-sep" />
        {/* 60 is ComputerEase's own field width, not a style choice (measured 2026-09-03 on LONGV-D-01: a 127-character
            scope arrived in CE cut to 60 mid-word, and that is what prints on the AIA pay application). */}
        <div className="ov-label">
          <span>Contract for</span>
          <span style={{ marginLeft: 'auto', marginRight: 10, color: forLen >= 60 ? 'var(--warning)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{forLen} / 60</span>
        </div>
        <InlineEdit value={edited?.contract_for || ''} placeholder="What the contract is for, e.g. LED lighting retrofit" maxLength={60}
          ariaLabel="Edit contract scope" onChange={(v) => saveField('contract_for', v)} openSignal={ask.key === 'contract_for' ? ask.n : 0} />
        <div style={{ fontSize: 12, color: 'var(--text-4)', margin: '2px 0 10px 10px' }}>Prints on AIA pay applications. ComputerEase cuts it at 60 characters.</div>
        <div className="ov-label">Description</div>
        <InlineEdit multiline value={edited?.description || ''} placeholder="A short description of this job" ariaLabel="Edit description"
          onChange={(v) => saveField('description', v)} openSignal={ask.key === 'description' ? ask.n : 0} />
      </div>
      <div title="Synced from the Schedule of Values — managed in the Contract Value tab" style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'var(--bg-surface-2)',
        borderTop: '1px solid var(--border-subtle)', borderRadius: '0 0 14px 14px',
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)' }}>Contract value</span>
        <span style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
          {(Number(contractVal) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-3)', padding: '2px 6px', borderRadius: 5, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>SOV · synced</span>
        <Icon name="lock" size={12} style={{ color: 'var(--text-4)' }} />
      </div>
    </div>
  );
}

function Settings({ edited, saveField }) {
  const ce = !!edited?.ce_integration_enabled;
  return (
    <div className="ov-card">
      <Head icon="gear" title="Settings" />
      <div className="ov-body">
        <div className="ov-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>Manual billing override</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.45 }}>Type % complete on pay app lines instead of taking it from the schedule. For cutover jobs.</div>
          </div>
          <Toggle on={!!edited?.manual_billing_override} onClick={() => saveField('manual_billing_override', edited?.manual_billing_override ? 0 : 1)} />
        </div>
        <div className="ov-sep" />
        <div className="ov-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>
              ComputerEase <StatusBadge kind={ce ? 'enabled' : 'disabled'} label={ce ? 'Linked' : 'Off'} />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.45 }}>
              {edited?.is_test ? 'A test job never syncs to ComputerEase.' : ce ? 'Pay apps sync to ComputerEase. Set when the job was created.' : 'Pay apps don’t sync to ComputerEase. Set when the job was created.'}
            </div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-4)', flex: 'none' }}><Icon name="lock" size={12} />Locked</span>
        </div>
      </div>
    </div>
  );
}

/** Tasks needing a progress update and the schedule's alerts: only when something needs attention. */
function Health({ alertsSummary, depAlerts, onOpenTab }) {
  const progressItems = (alertsSummary && alertsSummary.summaries) || [];
  const schedItems = ((depAlerts && depAlerts.alerts) || []).slice(0, 5);
  if (!progressItems.length && !schedItems.length) return null;
  const tasks = progressItems.reduce((a, s) => a + (s.alert_count || 0), 0);
  const critical = progressItems.reduce((a, s) => a + (s.critical_count || 0), 0);
  const count = tasks + schedItems.length;
  const pill = { marginLeft: 'auto', fontSize: 12, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', padding: '2px 8px', borderRadius: 999 };
  return (
    <div className="ov-card">
      <Head icon="alert-triangle" title="Project health" side={`${plural(count, 'item')} need attention`} />
      <div className="ov-body" style={{ display: 'grid', gap: 14 }}>
        {progressItems.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
              Tasks need progress updates<span className="mono" style={pill}>{plural(tasks, 'task')}{critical ? ` · ${critical} critical` : ''}</span>
            </div>
            <div style={{ background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 18%, transparent)', borderRadius: 10, overflow: 'hidden' }}>
              {progressItems.map((s, i) => (
                <div key={i} style={{ borderTop: i ? '1px solid color-mix(in oklch, var(--warning) 12%, transparent)' : 'none' }}>
                  <AlertRow severity={s.critical_count > 0 ? 'critical' : 'warning'} primary={s.period_name}
                    secondary={s.days_until_end <= 0 ? 'Period ended' : `${plural(s.days_until_end, 'day')} left`}
                    count={`${plural(s.alert_count, 'task')}${s.critical_count > 0 ? ` · ${s.critical_count} critical` : ''}`} />
                </div>
              ))}
            </div>
          </div>
        )}
        {schedItems.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
              Scheduling alerts<span className="mono" style={pill}>top {schedItems.length}{schedItems.filter((a) => a.severity === 'critical').length ? ` · ${schedItems.filter((a) => a.severity === 'critical').length} critical` : ''}</span>
            </div>
            <div style={{ background: 'color-mix(in oklch, var(--danger-soft) 50%, var(--bg-surface-2))', border: '1px solid color-mix(in oklch, var(--danger) 14%, transparent)', borderRadius: 10, overflow: 'hidden' }}>
              {schedItems.map((a, i) => (
                <div key={i} style={{ borderTop: i ? '1px solid color-mix(in oklch, var(--danger) 10%, transparent)' : 'none' }}>
                  <AlertRow severity={a.severity === 'critical' ? 'critical' : 'warning'} primary={a.message || a.type}
                    secondary={a.context || (a.type === 'circular' ? 'Circular dependency' : a.type === 'date_violation' ? 'Date violation' : 'Missing dates')} />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <button type="button" onClick={() => onOpenTab('tasks')} style={{ background: 'transparent', border: 'none', color: 'var(--accent-text)', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>
                View all in Schedule →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProjectOverview({
  projectId, edited, summary, billing, contractVal, projectNow, isTest, pmList = [], alertsSummary, depAlerts,
  logoUploading, onLogoUpload, onRemoveLogo, saveField, saveState, onOpenTab, workWeek, onWorkWeekChanged,
}) {
  const [ask, setAsk] = useState({ key: null, n: 0 });
  const detailsRef = useRef(null);
  const teamRef = useRef(null);
  const timeRef = useRef(null);
  const todayIso = ymdOf(projectNow || new Date());

  // A "Finish setting up" chip: bring its card into view and open its field.
  const onAsk = (key) => {
    const card = ['start_date', 'end_date', 'contract_date'].includes(key) ? timeRef
      : ROLES.some((r) => r.key === key) ? teamRef : detailsRef;
    if (card.current) card.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setAsk((a) => ({ key, n: a.n + 1 }));
  };

  const clock = clockOf(ymd(edited?.start_date) || ymd(summary?.earliest_task_start), ymd(summary?.latest_task_finish) || ymd(edited?.end_date), todayIso);
  return (
    <div className="ov">
      <style>{CSS}</style>
      <div className="ov-top">
        <Glance clock={clock} work={summary?.work_done_percent} billed={billing?.billed || 0} contract={contractVal || 0}
          collected={billing?.collected || 0} isTest={isTest} todayIso={todayIso} />
        <Setup edited={edited} onAsk={onAsk} />
      </div>
      <div className="ov-saveline"><SaveState state={saveState} /></div>
      <div className="ov-cols">
        <div className="ov-col">
          <Details edited={edited} saveField={saveField} ask={ask} cardRef={detailsRef} logoUploading={logoUploading}
            onLogoUpload={onLogoUpload} onRemoveLogo={onRemoveLogo} contractVal={contractVal} />
          <Team edited={edited} pmList={pmList} saveField={saveField} ask={ask} cardRef={teamRef} />
        </div>
        <div className="ov-col">
          <Health alertsSummary={alertsSummary} depAlerts={depAlerts} onOpenTab={onOpenTab} />
          <Timeline edited={edited} todayIso={todayIso} isTest={isTest} saveField={saveField} ask={ask} cardRef={timeRef}
            projectId={projectId} workWeek={workWeek} onWorkWeekChanged={onWorkWeekChanged} />
          <Settings edited={edited} saveField={saveField} />
        </div>
      </div>
    </div>
  );
}
